import { Item, RenderItemFormSidebarPanelCtx } from "datocms-plugin-sdk";
import { Button, Canvas } from "datocms-react-ui";
import { sendMessageToChatGPT } from "../utils/chatgpt";
import { useState } from "react";
import StoryGeneratorProgress, { StoryGenerationProgress } from "../components/StoryGeneratorProgress";
import { LogLevel, buildClient } from '@datocms/cma-client-browser';
import {Dast, createEnumerationDast, createParagraphs, createSimpleTextDast, createSimpleTitleDast} from "../utils/Dast";
import { StoryCardChatGPTAnswer, LocalizedDatoImage, LocalizedString, LocalizedStructuredText, StoryCardData, StoryData } from "../utils/common-types";

async function insertStoryCard(apiToken: string, storyCardData: StoryCardData): Promise<string> {
  const client = buildClient({ apiToken, logLevel: LogLevel.BASIC, });
  let newStoryCardId : string;
  try {
    const newStoryCard = await client.items.create({
      item_type: {
        type: "item_type",
        id: "LbNWM4RGQGW9g232h87p1Q"
      },
      text_before_image: storyCardData.textBeforeImage,
      image: null, // Since the image must be null
      text_after_image: storyCardData.textAfterImage,
      background_image: {
        upload_id: storyCardData.backgroundImageId,
      }
    });
    newStoryCardId = newStoryCard.id;
    console.log('Story card created:', newStoryCard);
    return newStoryCardId;
  } catch (error) {
    console.error('Error creating story card:', error);
    throw error;
  }
}

async function insertStory(apiToken : string, storyData : StoryData) : Promise<string> {
  const client = buildClient({ apiToken, logLevel: LogLevel.BASIC, });
  let newStoryId : string;
  try {
    const newStory = await client.items.create({
      item_type: {
        type: "item_type",
        id: "xo3ARDQ2QYiEMsfqWojbTg"
      },
      card_image: {
        upload_id: storyData.cardImageId
      },
      card_text: storyData.cardText,
      title_image: {
        upload_id: storyData.titleImageId,
      },
      title: storyData.titleText,
      story_cards: storyData.storyCards,
      button_text: storyData.buttonText,
      button_action: JSON.stringify(storyData.buttonAction),
      tags: storyData.tags
    });
    newStoryId = newStory.id;
    return newStoryId;
  } catch (error) {
    console.error('Error creating story:', error);
    throw error;
  }
}

function getDefaultTitleImageIdAndBackgroundColor() {
  const possibleTitleImageIds = [
    "cvIgNO5YSZi9r0qpLOj97Q", //#fce5ce
    "QcaFdaZfTaWpAasEuECPBA", //#ff7f66
    "NTim8SUhSiW-xLkELp_xjg", //#fcbc9c
    "W8f3Oi-bQjiGCWkjPFED3g", //#366363
    "nS064UDzRd2lmveRgiogtQ",
    "ONMG5shfRCayqIIZGkNIuA", //#71d3f4
  ];
  const rnd = Math.random();
  return possibleTitleImageIds[Math.floor(rnd*(possibleTitleImageIds.length))];
}

async function generateStoryFromArticle(itemId : string, formValues: Record<string, unknown>, apiToken: string | undefined, setProgress?: (progress: StoryGenerationProgress) => void) {
  const title = formValues.title as LocalizedString;
  const headline = formValues.headline as LocalizedString;
  const paragraph1 = formValues.paragraph1 as LocalizedStructuredText;
  const image1 = formValues.image1 as LocalizedDatoImage;
  const paragraph2 = formValues.paragraph2 as LocalizedStructuredText;
  const image2 = formValues.image1 as LocalizedDatoImage;
  const paragraph3 = formValues.paragraph3 as LocalizedStructuredText;
  const image3 = formValues.image1 as LocalizedDatoImage;
  const paragraph4 = formValues.paragraph4 as LocalizedStructuredText;
  const tags = formValues.tags as string;

  const backgroundImageId = getDefaultTitleImageIdAndBackgroundColor();

  console.log("backgroundImageId", backgroundImageId)
  console.log("Value of image1: ", image1);

  const numberOfStoryCards = 3;

  const prompt = `You must convert a long article to a short version which will be readed on cards.
	You must generate ${numberOfStoryCards} cards. Every card must contain 4-9 sentences and must write about a specific topic mentioned in the article.
	You don't have to follow the article, but the generated short version is a teaser for it.

	Your answer must be a JSON in the following format:
	{
		"title": "A short title for the whole story. Can be shorter than the title of the article",
		"short": "A short summery about this 'story'. Must be maximum 10 words long. (Basically this is a long title)",
		"cards": [
			{
				"type": "text",
				"subtitle": "The subtitle of the card. This will be the first sentence formatted as heading. It must be brief and describe the topic of this card",
				"content": "4-7 sentences"
			},
			{
				"type": "enumeration",
				"subtitle": "The subtitle of the card. This will be the first sentence formatted as heading. It must be brief and describe the topic of this card",
				"content": {
					"introduction": "A short describtion about the enumeration. Can be empty.",
					"bulletpoints": ["first bulletpoint", "second bulletpoint", "further bulletpoints, max 7"]
				}
			},
		]
	}

	Here is the full story: 

	Title: ${title.en}
	Headline: ${headline.en}
	Body: ${JSON.stringify(paragraph1.en) + JSON.stringify(paragraph2.en) + JSON.stringify(paragraph3.en) + JSON.stringify(paragraph4.en)}
	`;

  console.log("Prompt generated", prompt);
  // TODO It works but slow. Must show a progress bar
  if (setProgress) setProgress("waitForChatGPT");
  const answer = await sendMessageToChatGPT({ context: prompt, messages: [], jsonResponse: true }) as StoryCardChatGPTAnswer;
  if (setProgress) setProgress("uploadingStoryCards");
  if (setProgress) setProgress("idle");
  console.log(answer);
  if (apiToken == undefined) {
    console.log("I can't upload it, the apiToken is undefined");
    return;
  }

  const cardIds : string[] = [];  
  for (const cardData of answer.cards) {
    if (cardData.type === "text") {
      const textBeforeImage = createSimpleTitleDast(cardData.subtitle);
      const textAfterImage = createSimpleTextDast([cardData.content]);
      
      const cardId = await insertStoryCard(apiToken, {
        textBeforeImage: {en: textBeforeImage},
        textAfterImage: {en: textAfterImage },
        backgroundImageId
      });
      cardIds.push(cardId);
    } else if (cardData.type === "enumeration") {
      const textBeforeImage = createSimpleTitleDast(cardData.subtitle);
      const textAfterImage = createEnumerationDast(cardData.content.bulletpoints);
      textAfterImage.document.children = [...createParagraphs([cardData.content.introduction]), ...textAfterImage.document.children]
      const cardId = await insertStoryCard(apiToken, {
        textBeforeImage: {en: textBeforeImage},
        textAfterImage: {en: textAfterImage },
        backgroundImageId,
      });
      cardIds.push(cardId);
    }
  }

  const story : StoryData = {
    titleText: {en: answer.short},
    cardText: {en: answer.title},
    cardImageId: image1.en?.upload_id ?? backgroundImageId,
    titleImageId: backgroundImageId,
    storyCards: cardIds,
    buttonText: {en: "Read more"},
    buttonAction: {
      type: "openUrl",
      value: `https://perisyncarticles.com/?locale=en&id=${itemId}`
    },
    tags,
  }
  const storyId = await insertStory(apiToken, story);
  if (window.top === null) {
    alert("I can't redirect you to the created story, the top object does not exists")
  } else {
    window.top.location.href=`https://cycle-tracker-backend.admin.datocms.com/editor/item_types/xo3ARDQ2QYiEMsfqWojbTg/items/${storyId}`;
  }
}

type Props = {
  ctx: RenderItemFormSidebarPanelCtx,
};

function ArticleToStorySidePanel({ ctx }: Props) {
  const [progress, setProgress] = useState<StoryGenerationProgress>("idle");

  const itemId = ctx.item?.id;

  return (
    <Canvas ctx={ctx}>
      <div className="input-label">Number of story cards</div>
      <input id="numberOfStory" type="number" defaultValue={3} />
      <div className="input-label">Style of story</div>
      <div>
        {
          (itemId !== undefined)?
            <Button onClick={() => generateStoryFromArticle(itemId, ctx.formValues, ctx.currentUserAccessToken, setProgress)}>Generate story</Button>
          :
            <span>The item does not exist.</span>
        }    
      </div>
      <div className="progress">
        <StoryGeneratorProgress progress={progress} />
      </div>
    </Canvas>
  );
};

export default ArticleToStorySidePanel;

